'use client';

import { c as _c } from "react/compiler-runtime";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import type { LabsReadoutHandoff } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { BrandButton, Confetti } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/_components';
import { DurationTag, RecTag } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-tag';
import { LogoChip, VerticalThumbnail } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/rec-thumbnail';
import { StepShell } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/step-shell';
import type { LabsRecommendation } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';
import { LABS_PREVIEW_EVENTS } from '@/lib/analytics/labs-preview-events';
import { cn } from '@/utils/cn';
import { isEmailAlreadyCaptured, isEmbedded } from '../_engine/entry-placement';
import { isFieldLessPack } from '../_engine/field-less-pack';
import { usePreviewAnalytics } from '../_engine/preview-analytics';
import { artifactFileName, buildPreviewArtifact, renderArtifactHtml } from '../_engine/preview-artifact';
import { type SignUpParams, signUpHref } from '../_engine/sign-up-href';
import { isLeadPipeConfigured, submitPreviewLead } from '../_engine/submit-preview-lead';
import { PreviewMatchModal } from './preview-match-modal';

/**
 * The public preview's last screen: the result, the externships it points to, and the ask.
 *
 * It occupies the `recommendations` step slot, where the gated sprint shows its own exit screen. The
 * two are different because the reader is different. The sprint sends a paying learner into a program
 * they can start today. This one is talking to someone who arrived from a search result minutes ago
 * and has no account, so every one of the sprint's CTAs would bounce them to /payment.
 *
 * TWO PATHS, NOT A GATE (Jay, 2026-08-26). The result is never withheld. An earlier draft put the
 * matched externships behind the email, and it optimised the wrong thing: signups are what this
 * experiment is judged on, and emails are worth much less while there is no lead endpoint and no
 * nurture list wired up. A gate taxes the reader who was ready to sign up in order to harvest an
 * address from the reader who was not.
 *
 * So the page offers both, in order of what they are worth:
 *   1. Each matched externship opens IN PLACE to show what the work actually is, and the trial CTA
 *      lives inside that panel. Three beats, not one: curious, informed, converted.
 *   2. The email ask sits below, framed as the alternative for someone who is not ready.
 *
 * The cards cannot open the catalog: every program page lives inside (subscription-gated), so a link
 * would bounce a signed-out reader straight to /payment. Expanding in place is how they get the
 * substance anyway, without a navigation that would break the flow or a wall that would end it.
 *
 * Recommendations come from the pack's static fallback, not the live catalog. `useLabsRecommendations`
 * fetches through `useQueryAuth`, which has no session here, so it would fail on every load and land
 * on this same fallback having spent a request and a loading state to get there. The fallback names
 * real programs by contract, so the cards are honest either way.
 */

/**
 * The hook is the TRIAL, not the price (Jay, 2026-08-26). There is no public pricing page, it sits
 * behind the paywall, and leading with a number asks someone who has never heard of Extern to judge a
 * cost before they know the value. Seven days free is the offer.
 *
 * Stated HERE, once, next to the matches rather than inside the overlay. The overlay is the real
 * product component and its button says "Start now"; forking it just to reword one label would mean
 * this page stops tracking whatever the product does next. The card requirement still gets said out
 * loud before anyone taps, which is the part that matters, because finding it at checkout is how
 * goodwill gets spent.
 */
const TRIAL_TERMS = 'Free for 7 days. Card required, cancel any time.';

/**
 * Minimal email shape check, deliberately not the platform's `emailSchema`. This is a marketing
 * capture: over-rejecting a real address costs a signup, while accepting a typo costs one dead row.
 * Permissive is the right direction here.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * One matched program. Tapping it opens the detail OVERLAY; it never navigates.
 *
 * It used to send every tap straight to sign-up, which is a bait: a reader taps the one program they
 * were curious about and lands on a bare account form that says nothing about it. Both design reviews
 * scored that as the biggest drop on the page, at the moment intent is highest.
 *
 * The in-place accordion that replaced it was worse to look at: opening a card pushed the rest of the
 * page down and the reader lost their place. The overlay leaves the payoff still underneath.
 */
function MatchCard(t0) {
  const $ = _c(34);
  const {
    accent,
    onOpen,
    rec
  } = t0;
  let t1;
  if ($[0] !== onOpen || $[1] !== rec) {
    t1 = () => onOpen(rec);
    $[0] = onOpen;
    $[1] = rec;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== accent || $[4] !== rec.company || $[5] !== rec.image || $[6] !== rec.logo) {
    t2 = <VerticalThumbnail accent={accent} company={rec.company} logo={rec.logo} src={rec.image} />;
    $[3] = accent;
    $[4] = rec.company;
    $[5] = rec.image;
    $[6] = rec.logo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== rec.company || $[9] !== rec.logo) {
    t3 = <LogoChip className="absolute -bottom-5 left-4" company={rec.company} logo={rec.logo} />;
    $[8] = rec.company;
    $[9] = rec.logo;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="relative">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== rec.title) {
    t5 = <span className="font-instrument text-[20px] text-fs-text leading-[1.15]">{rec.title}</span>;
    $[14] = rec.title;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== rec.blurb) {
    t6 = <span className="text-fs-body-md text-fs-text-subtle leading-relaxed">{rec.blurb}</span>;
    $[16] = rec.blurb;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== rec.duration) {
    t7 = <DurationTag>{rec.duration}</DurationTag>;
    $[18] = rec.duration;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== rec.tags) {
    t8 = rec.tags.slice(0, 2).map(_temp);
    $[20] = rec.tags;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-fs-xs pt-fs-xs">{t7}{t8}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="flex items-center gap-1 pt-fs-xs font-medium text-fs-body-md text-fs-brand">See what you would actually do<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></span>;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t9) {
    t11 = <div className="flex flex-1 flex-col gap-fs-xs p-fs-md pt-8">{t5}{t6}{t9}{t10}</div>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t1 || $[31] !== t11 || $[32] !== t4) {
    t12 = <button className="group flex cursor-pointer flex-col overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface text-left shadow-[0_1px_2px_rgba(10,14,26,0.04)] transition-shadow hover:shadow-[0_8px_24px_rgba(10,14,26,0.08)]" onClick={t1} type="button">{t4}{t11}</button>;
    $[30] = t1;
    $[31] = t11;
    $[32] = t4;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp(tag) {
  return <RecTag key={tag}>{tag}</RecTag>;
}
export function PreviewPayoff(): ReactNode {
  const pack = usePack();
  const {
    archetypes,
    axis,
    deriveArchetype,
    displayName,
    recommendations: config,
    slug
  } = pack;
  const storedArchetype = useLabsStore(s => s.archetype);
  const m2Answers = useLabsStore(s_0 => s_0.m2Answers);
  const m1Answers = useLabsStore(s_1 => s_1.m1Answers);
  const m3Answers = useLabsStore(s_2 => s_2.m3Answers);
  const m3Diagnosis = useLabsStore(s_3 => s_3.m3Diagnosis);
  const {
    guideSource,
    placement,
    track,
    visitorId
  } = usePreviewAnalytics();
  // Whether this reader already handed over an address on the guide page (the newsletter
  // confirmation link). When true the readout is NOT gated: asking twice in one visit is friction
  // that buys us a duplicate we already hold. See `entry-placement.ts`, including why the address
  // itself is deliberately not passed to us.
  const emailAlreadyCaptured = isEmailAlreadyCaptured(placement);
  // Running inside the guide page's frame. Only affects WHERE navigation lands; every other
  // behaviour on this screen is identical.
  const embedded = isEmbedded(placement);
  const [openMatch, setOpenMatch] = useState<LabsRecommendation | null>(null);
  const [email, setEmail] = useState('');
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  /** The rendered readout while it is being shown in the overlay, or null when closed. */
  const [artifactPreview, setArtifactPreview] = useState<string | null>(null);

  /**
   * 🔴 QA-521. While the readout overlay is open the page behind it must not scroll. On mobile
   * Safari a swipe that runs past the end of the iframe's content chains to the page, the page
   * moves under the fixed overlay, and every gesture after that keeps scrolling the page, so the
   * reader is stuck at the bottom of a readout they can no longer scroll. Pinning the body with
   * `position: fixed` is the technique that actually holds on iOS, where `overflow: hidden` alone
   * does not; the saved offset puts the reader back exactly where they were on close.
   */
  useEffect(() => {
    if (artifactPreview === null) {
      return;
    }
    const {
      body
    } = document;
    const scrollY = window.scrollY;
    const previousCss = body.style.cssText;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.cssText = previousCss;
      window.scrollTo(0, scrollY);
    };
  }, [artifactPreview]);

  // Prefer the store's derived archetype, then fall back to re-deriving from the raw answers, exactly
  // as the sprint's reveal screen does. Without it a reader on a restored draft gets a result card
  // with a blank name, on the one screen where an empty headline costs the whole payoff.
  const archetypeId = storedArchetype ?? deriveArchetype(m2Answers);
  const arch = archetypes[archetypeId];
  const accent = arch?.accent ?? axis.bothAccent;

  // The pack's curated list for this archetype. Empty rather than backfilled from another archetype:
  // showing programs picked for someone else would quietly undo the one bit of personalisation here.
  const matches = useMemo<LabsRecommendation[]>(() => [...(config.fallback[archetypeId] ?? [])], [archetypeId, config]);

  // What the reader takes away. Built from what they actually did, so it is empty-tolerant by design.
  const artifact = useMemo(() => buildPreviewArtifact(pack, {
    archetype: storedArchetype,
    m1Answers,
    m2Answers,
    m3Answers,
    m3Diagnosis
  }), [m1Answers, m2Answers, m3Answers, m3Diagnosis, pack, storedArchetype]);

  // Describes what is actually in the file, so the ask names a real thing rather than promising one.
  // Every clause is conditional because a reader who skipped the work must not be told they did it.
  // Whether the lead pipe exists in this build. It records the address; it does NOT email the readout,
  // because that endpoint is public and unauthenticated and mailing from it would make it an open
  // relay. So nothing on this screen promises an inbox copy. The only forward promise is new
  // externships in the track, which Customer.io sends off the sheet.
  const leadCaptureOn = isLeadPipeConfigured();

  // Describes what is actually in the file, so the ask names a real thing rather than promising one.
  // Every clause is conditional because a reader who skipped the work must not be told they did it.
  const artifactSummary = useMemo(() => {
    const parts: string[] = [artifact.archetypeName || 'your result'];
    if (artifact.fingerprint.length > 0) {
      parts.push('your mindset fingerprint');
    }
    if (artifact.score.answered > 0) {
      const rateWord = artifact.score.answered === 1 ? 'rate' : 'rates';
      parts.push(`the ${artifact.score.answered} ${rateWord} you worked out on ${artifact.subjectName}`);
    }
    if (artifact.diagnosis) {
      parts.push('your own read of the case');
    }
    const last = parts.pop();
    const list = parts.length > 0 ? `${parts.join(', ')} and ${last}` : last ?? '';
    return `A designed two-page readout: ${list}.`;
  }, [artifact]);

  // The denominator for the email ask. ⚠️ IT FIRES ON PAYOFF MOUNT, so today it equals COMPLETED: the
  // ask was deliberately moved ABOVE the externship cards, which puts it on screen for everyone who
  // reaches this step. That redundancy is the point of the move, not an oversight. It stays a separate
  // event so the two diverge honestly if the ask is ever pushed below the fold again.
  const artifactShown = useRef(false);
  useEffect(() => {
    if (artifactShown.current) {
      return;
    }
    artifactShown.current = true;
    track(LABS_PREVIEW_EVENTS.ARTIFACT_SHOWN, {
      archetype: archetypeId,
      has_diagnosis: artifact.diagnosis !== '',
      rates_answered: artifact.score.answered
    });
  }, [archetypeId, artifact, track]);

  // One render path for the download AND the emailed copy, so the file in their inbox is
  // byte-identical to the one in their downloads folder. Two renderers is how the two would drift.
  const renderArtifactNow = (): string => {
    // Long-form date, because the file is a keepsake and "8/28/26" on a keepsake reads like a receipt.
    const completedOn = new Date().toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    return renderArtifactHtml(artifact, completedOn);
  };

  /** How long the object URL must outlive the click for a newly opened tab to finish loading it. */
  const OBJECT_URL_TTL_MS = 60_000;

  /**
   * Save the file to their downloads. Seeing it is `showArtifact`'s job.
   *
   * 🔴 SHOWING IT MATTERS AS MUCH AS SENDING IT. A download alone puts a designed two-page readout
   * into a Downloads folder, where most readers will never look at it: we would have built the thing
   * whose whole job is to be wanted, and then hidden it. Seeing it lands the payoff in the second they
   * earned it, which is also the second they decide whether to make an account.
   *
   * The auto-open is BEST EFFORT and nothing in the copy claims it happened. Pop-up blockers, mobile
   * Safari and embedded webviews all refuse `window.open` in ways that cannot be detected reliably, so
   * promising a tab would be exactly the kind of promise-we-do-not-keep this screen has already been
   * burned by once. The guaranteed path is the explicit "Open it" button below, which is a direct user
   * gesture and always works; the download happens either way.
   */
  const deliverArtifact = (): void => {
    const blob = new Blob([renderArtifactNow()], {
      type: 'text/html'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = artifactFileName(artifact);
    link.href = url;
    link.click();

    // Revoked on a timer, not immediately: a tab opened above is still fetching this URL, and
    // revoking synchronously would leave the reader looking at a blank page.
    window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
  };

  /**
   * Show the readout, in page.
   *
   * 🔴 AN OVERLAY, NOT A NEW TAB. A tab was the first instinct and it is not reliable: pop-up blockers,
   * mobile Safari and in-app webviews all refuse `window.open` in ways the page cannot detect, so the
   * reader most likely to be blocked is the phone reader arriving from Google, which is most of this
   * audience. An overlay cannot be blocked, works identically on every device, and keeps them on the
   * page where the account ask is.
   *
   * `sandbox` with no permissions: the document is ours and carries no script, but it does embed text
   * the reader typed, so the strictest setting is free insurance.
   */
  const showArtifact = (): void => {
    setArtifactPreview(renderArtifactNow());
  };

  /**
   * The reader took their file. Fired from the two paths that HAND IT OVER, once each, rather than
   * from the helpers: unlocking used to call both `deliverArtifact` and `showArtifact` and each fired
   * this, so one unlock logged two downloads with opposite `opened` values and the event stopped
   * measuring anything. Found by Bugbot on #2843.
   */
  const trackArtifactTaken = (opened: boolean): void => {
    track(LABS_PREVIEW_EVENTS.ARTIFACT_DOWNLOADED, {
      archetype: archetypeId,
      opened
    });
  };

  // Taken from the unlocked panel, after they have their write-up. Its own `path` so the board can
  // answer whether the artifact earns signups or only earns addresses, which is the whole bet.
  const handleAccountFromArtifact = (): void => {
    track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
      archetype: archetypeId,
      path: 'artifact'
    });
    // 🔴 THE UNGATED PATH MUST NOT SEND A CONSENT ANSWER. On the newsletter path there is no email
    // box and no checkbox, so `email` is '' and `consented` is its `false` DEFAULT — a value nobody
    // chose. Forwarding it would write `newsletter=0` for a reader who opted IN to the newsletter
    // seconds earlier on the guide page, silently reversing a real consent decision. Omitting both
    // leaves sign-up on its own default. This is the same class of bug Bugbot found on #2843, where
    // the control sign-up form re-defaulted a decline back to true.
    if (emailAlreadyCaptured) {
      goToSignUp({});
      return;
    }
    goToSignUp({
      email: email.trim(),
      newsletter: consented
    });
  };
  const goToSignUp = (params: Omit<SignUpParams, 'guideSource' | 'readout' | 'vertical'>): void => {
    // The result rides along on every account hand-off from this screen, whichever button it was.
    const readout: LabsReadoutHandoff = {
      archetypeId,
      // Positional, one slot per slider in pack order, null where they skipped: the artifact drops
      // unanswered sliders, which would shift later dots onto the wrong questions on the rail.
      fingerprint: pack.m2.questions.map(question => {
        const value = m2Answers[question.id];
        return value === undefined || !Number.isFinite(value) ? null : value;
      }),
      lean: artifact.lean ? {
        aCount: artifact.lean.aCount,
        bCount: artifact.lean.bCount
      } : null,
      score: artifact.score
    };
    const href = signUpHref({
      ...params,
      guideSource,
      placement,
      readout,
      vertical: slug
    });

    // 🔴 BREAK OUT OF THE FRAME. Embedded, `window.location` is the FRAME's location, so this would
    // load a full-viewport sign-up page into a 690px window inside an article: the reader would see
    // a cropped form with the guide page still around it and no way to tell what happened. The whole
    // point of the account handoff is that it leaves the guide page.
    //
    // `window.top` is same-origin-restricted for READING, but ASSIGNING to `top.location` is allowed
    // for a frame whose ancestor permitted it, which is exactly our case. Falling back to
    // `window.location` keeps the standalone route working and covers the case where a browser
    // refuses the top-level navigation.
    if (embedded) {
      try {
        // Two separate things are wrong with the obvious version of this line, and BOTH are invisible
        // to same-origin testing, which is what the harness is. Found by review, not by driving it.
        //
        // 🔴 1. ABSOLUTE, NOT ROOT-RELATIVE. `href` is `/sign-up?...`. A relative URL resolves against
        // the TOP document, which is the guide page on www.extern.com rather than this frame on
        // app.extern.com, so root-relative sent every account handoff to www.extern.com/sign-up.
        //
        // 🔴 2. `href =`, NOT `.assign()`. A cross-origin `Location` exposes only the `href` SETTER and
        // `replace()`. `assign` is not on that allowlist and throws a SecurityError, which the catch
        // below would swallow, dropping the reader into an in-frame navigation: a full sign-up form
        // loaded inside the modal, which is the exact failure this branch exists to prevent. Verified
        // in a real cross-origin frame: `assign` throws, `replace` is callable, `href` is writable.
        //
        // `href` rather than `replace` because it leaves a history entry, so Back returns the reader
        // to the guide page they were reading. `replace` would erase it.
        (window.top ?? window).location.href = new URL(href, window.location.origin).toString();
        return;
      } catch {
        // Fall through to the in-frame navigation rather than stranding them on a dead button.
      }
    }

    // A hard navigation, not a client push. Sign-up lives in another route group with its own layout
    // and auth providers, and this reader has no session for a soft transition to preserve.
    window.location.assign(href);
  };

  // Opening a card is INTENT, not conversion, so it gets its own event and no navigation. Expanded
  // over shown answers "did anyone care about the matches", which decides whether they earn their
  // place on this screen at all.
  const handleMatchOpen = (rec: LabsRecommendation): void => {
    setOpenMatch(rec);
    track(LABS_PREVIEW_EVENTS.RECOMMENDATION_CLICKED, {
      archetype: archetypeId,
      company: rec.company,
      externship_id: rec.externshipId ?? null,
      externship_title: rec.title
    });
  };
  const handleStartTrial = (rec_0: LabsRecommendation): void => {
    // Stamped as its own path so the board can answer which of the two asks actually converts. If
    // every signup arrives via a card, the email form is dead weight and should go.
    track(LABS_PREVIEW_EVENTS.SIGNUP_HANDOFF, {
      archetype: archetypeId,
      externship_title: rec_0.title,
      path: 'externship_card'
    });
    goToSignUp({
      externshipId: rec_0.externshipId
    });
  };

  /**
   * Hand the readout over and record that we did. Shared by both paths so they cannot drift: the
   * gated path reached it by giving an address, the newsletter path because one was already given.
   * `gated` is stamped so the board can read the two conversion rates apart rather than pooling a
   * step that cost an email with one that did not.
   */
  const completeUnlock = (gated: boolean): void => {
    track(LABS_PREVIEW_EVENTS.ARTIFACT_UNLOCKED, {
      archetype: archetypeId,
      gated,
      has_diagnosis: artifact.diagnosis !== '',
      // Whether an inbox copy was even possible, so a conversion difference between builds with and
      // without the pipe reads as the delivery promise's effect rather than noise.
      lead_capture: leadCaptureOn
    });
    setUnlocked(true);
    setSubmitting(false);
    deliverArtifact();
    showArtifact();
    trackArtifactTaken(true);
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    // The newsletter path: no address to validate, nothing to record as a lead, and above all NO
    // `EMAIL_SUBMITTED`. Firing that with no email would inflate the one number round one is judged
    // on with rows that captured nothing, which is worse than not measuring the path at all.
    if (emailAlreadyCaptured) {
      setError(null);
      completeUnlock(false);
      return;
    }
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError('That email does not look right. Mind checking it?');
      return;
    }
    setError(null);
    setSubmitting(true);

    // The lead is recorded TWICE, on purpose, because the two copies answer different questions.
    // The event is the MEASUREMENT copy: it lands in Mixpanel next to the rest of the funnel, so
    // "what share of finishers give an address" is one query. The spreadsheet is the MARKETING copy,
    // which Customer.io imports from directly. Neither can block the reader reaching sign-up, and if
    // the two ever disagree the event is the one to trust, since the beacon reports no failures.
    // 🔴 THE CHECKBOX IS OPTIONAL, AND THAT IS A PRIVACY DECISION, NOT A UX ONE. Requiring marketing
    // consent to receive your own work is consent bundling, the exact pattern the privacy-consent work
    // exists to kill. The readout delivery is transactional; the checkbox governs only marketing use,
    // and its real state is recorded so Customer.io can filter on it honestly.
    track(LABS_PREVIEW_EVENTS.EMAIL_SUBMITTED, {
      archetype: archetypeId,
      email: trimmed,
      marketing_consent: consented
    });
    submitPreviewLead({
      archetype: archetypeId,
      email: trimmed,
      guideSource,
      marketingConsent: consented,
      vertical: slug,
      visitorId
    });

    // 🔴 UNLOCKS IN PLACE. IT DOES NOT NAVIGATE. This used to send the reader straight to sign-up the
    // instant they submitted, which meant the email bought them nothing and the promise on the form
    // ("we will send you your result") was kept by a mail we do not yet send. They asked for their
    // work; give them their work. The account ask stays on screen underneath, where it now follows
    // something we delivered rather than replacing it.
    completeUnlock(true);
  };
  return <StepShell contentAlign="top" showProgress={false}>
      <PreviewMatchModal onClose={() => setOpenMatch(null)} onStartTrial={handleStartTrial} rec={openMatch} />

      {artifactPreview === null ? null : <div className="fixed inset-0 z-50 flex flex-col bg-black/70 p-2 sm:p-6">
          {/* Header and iframe are one clipped card with a SOLID header bar. The first two cuts
              floated the header straight on the translucent scrim, and on a phone the transparent
              button plus whatever sat behind the scrim read as the title rendering behind the
              controls (Felipe, QA-520 re-test). Nothing shows through a solid bar. `flex-wrap` lets
              the buttons drop below the title on narrow screens instead of fighting it for a single
              line. */}
          <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden rounded-fs-card">
            <div className="flex flex-wrap items-center justify-between gap-x-fs-sm gap-y-1 bg-fs-text px-3 py-2 sm:px-4">
              <span className="font-semibold text-fs-body-lg text-white">Your readout</span>
              {/* Weight follows intent: Download solid, since it is the action the reader traded
                  their email for (QA-520); Close a visible ghost, filled enough to read as a
                  control, quiet enough to stay secondary. Both compacted to header scale. */}
              <div className="flex gap-fs-xs">
                <BrandButton className="px-4 py-2 text-[16px]" onClick={deliverArtifact} type="button">
                  Download
                </BrandButton>
                <BrandButton className="border-white/40 bg-white/10 px-4 py-2 text-[16px] text-white hover:bg-white/20" onClick={() => setArtifactPreview(null)} type="button" variant="outline">
                  Close
                </BrandButton>
              </div>
            </div>
            <iframe className="w-full flex-1 bg-white" sandbox="" srcDoc={artifactPreview} title="Your Extern Labs readout" />
          </div>
        </div>}

      <div className="relative flex flex-col gap-fs-lg py-fs-md">
        <Confetti accent={accent} />

        {/* The result. Same language as the sprint's reveal, so a reader who subscribes later
            recognises the product they already met. */}
        <div className="relative overflow-hidden rounded-fs-card border border-fs-border bg-fs-surface p-7 shadow-[0_1px_2px_rgba(10,14,26,0.04),0_8px_24px_rgba(10,14,26,0.05)]">
          <span aria-hidden className="absolute inset-y-0 left-0 w-1" style={{
          backgroundColor: accent
        }} />
          <div className="flex items-center gap-3">
            <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-fs-pill text-fs-on-primary" style={{
            backgroundColor: accent
          }}>
              <Sparkles className="size-5" strokeWidth={2} />
            </span>
            <div className="flex flex-col">
              <span className="font-medium text-[12px] uppercase leading-[14px] tracking-[0.06em]" style={{
              color: accent
            }}>
                Your result
              </span>
              <span className="font-instrument text-[30px] leading-[1.1] tracking-[-0.5px]" style={{
              color: accent
            }}>
                {arch?.displayName}
              </span>
              <span className="mt-1 text-fs-body-md text-fs-text-subtle italic">{arch?.tagline}</span>
            </div>
          </div>
          <p className="mt-4 text-fs-body-lg text-fs-text-subtle leading-relaxed">{arch?.body}</p>
        </div>

        {/* 🔴 ABOVE THE MATCHES, DELIBERATELY. This block used to sit last, under two full-height
            externship cards, which put the only ask on the page below the fold on every laptop: Jay hit
            the payoff in testing and never saw it. Order now follows the reader rather than our funnel.
            Give them the thing they earned, then show them where it leads.
             THE TAKEAWAY, AND THE ONE THING ON THIS PAGE THAT IS GATED (with one exception: a reader
            arriving from the newsletter confirmation has already paid the price on the guide page, so
            for them it is not gated at all — see `entry-placement.ts`).
            Everything above is free: the archetype, the read, the matched externships. A reader who has
            never heard of Extern still gets the whole explanation without paying for it, because on a
            page reached from a Google result the explanation IS the pitch. What the email buys is a NEW
            thing we are adding, not something taken away and sold back. Keep that line where it is. */}
        {unlocked ? <div className="flex flex-col gap-fs-sm rounded-fs-card border border-fs-border bg-fs-surface-subtle p-fs-md">
            <div className="flex flex-col gap-1">
              <h2 className="font-semibold text-fs-text text-fs-title-lg">Your readout is ready</h2>
              <p className="text-fs-body-md text-fs-text-subtle">
                Saved to your downloads. Print it to PDF to keep it anywhere.
              </p>
            </div>
            <div className="flex flex-wrap gap-fs-xs">
              {/* The reliable way to actually LOOK at it. The auto-open on unlock is best effort and
                  silently fails under pop-up blockers, so this button is what guarantees the reader
                  can see what they earned. */}
              <BrandButton onClick={() => {
            showArtifact();
            trackArtifactTaken(true);
          }} type="button" variant="outline">
                Open it
              </BrandButton>
              <BrandButton onClick={() => {
            deliverArtifact();
            trackArtifactTaken(false);
          }} type="button" variant="outline">
                Download it again
              </BrandButton>
            </div>

            {/* 🔴 THE WARMEST MOMENT ON THE PAGE, AND IT USED TO BE DEAD SPACE. Before the artifact
                existed, the email form handed straight off to sign-up. Making it unlock in place was
                right for the reader and removed the only nudge from email to account, leaving a
                sentence where a button belonged.
                 It says ACCOUNT, not trial, and carries no card language on purpose. Signing up does not
                require a card; the TRIAL does, and `TRIAL_TERMS` still sits beside the externship
                matches where that ask actually lives. Collapsing the two is what makes a free step look
                like a paid one. */}
            <div className="flex flex-col gap-fs-xs border-fs-border border-t pt-fs-sm">
              {/* 🔴 THE EMAIL CLAIM IS ONLY TRUE ON THE GATED PATH. The newsletter path never handed us
                  the address (it is deliberately not in the URL, see `entry-placement.ts`), so
                  `handleAccountFromArtifact` cannot prefill sign-up for them. Promising "we already
                  have your email, one step left" and then showing an empty field is a small bait, and
                  the reader who notices trusts the next screen less. Found by Bugbot on #2898. */}
              <p className="text-fs-body-md text-fs-text-subtle">
                {emailAlreadyCaptured ? 'That was the short version. The full one is a real project with a real company, and it takes one step to start.' : 'That was the short version. The full one is a real project with a real company, and we already have your email, so there is one step left.'}
              </p>
              <BrandButton className="self-start" icon={ArrowRight} onClick={handleAccountFromArtifact} type="button">
                Create my free account
              </BrandButton>
            </div>
          </div> : <form className="flex flex-col gap-fs-sm rounded-fs-card border border-fs-border border-dashed bg-fs-surface-subtle p-fs-md" onSubmit={handleSubmit}>
            {/* The teaser: a shrunken mock of the readout's cover, in THEIR accent with THEIR result on
                it. Desire needs something to look at; a paragraph describing a file is not that. */}
            <div className="flex items-start gap-fs-md">
              <div aria-hidden className="w-[4.5rem] shrink-0 overflow-hidden rounded-lg border border-fs-border shadow-sm">
                <div className="flex flex-col gap-1.5 p-2.5" style={{
              backgroundColor: '#0b0e1a'
            }}>
                  <span className="block h-1 w-5 rounded-full" style={{
                backgroundColor: accent
              }} />
                  <span className="block font-bold text-[8px] uppercase leading-[1.15] tracking-wide" style={{
                color: accent
              }}>
                    {arch?.displayName}
                  </span>
                  <span className="block h-0.5 w-full rounded bg-white/25" />
                  <span className="block h-0.5 w-3/4 rounded bg-white/25" />
                  <span className="block h-0.5 w-5/6 rounded bg-white/25" />
                </div>
                {/* The white sliver reads as page two peeking out from under the cover. */}
                <div className="flex flex-col gap-1 bg-white p-1.5">
                  <span className="block h-0.5 w-2/3 rounded bg-fs-border" />
                  <span className="block h-0.5 w-full rounded bg-fs-border" />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="font-semibold text-fs-text text-fs-title-lg">
                  {emailAlreadyCaptured ? 'Your readout is ready' : 'Keep your readout'}
                </h2>
                <p className="text-fs-body-md text-fs-text-subtle">
                  {artifactSummary}{' '}
                  {emailAlreadyCaptured ? 'You are already on our list, so it is yours. No email needed.' : 'Add your email and it downloads right away.'}
                </p>
              </div>
            </div>

            {/* 🔴 NO FIELD AND NO CHECKBOX ON THE NEWSLETTER PATH. The address is already in Webflow
                and Customer.io; a second box would ask a reader who subscribed thirty seconds ago to
                prove it. The consent checkbox goes with it, because their answer is already recorded
                on the guide page and a fresh unticked box here would read as a new decline. */}
            {emailAlreadyCaptured ? <BrandButton className="self-start" disabled={submitting} type="submit">
                {submitting ? 'One moment' : 'Open my readout'}
              </BrandButton> : <>
                <div className="flex flex-col gap-fs-xs sm:flex-row">
                  <input aria-label="Email" autoComplete="email" className="flex-1 rounded-fs-button border border-fs-border bg-fs-surface px-4 py-3 text-fs-body-lg text-fs-text outline-none focus:border-fs-primary" id="labs-preview-email" inputMode="email" onChange={e => setEmail(e.target.value)} placeholder="you@university.edu" type="email" value={email} />
                  <BrandButton className="shrink-0" disabled={submitting} type="submit" variant="outline">
                    {submitting ? 'One moment' : 'Get my readout'}
                  </BrandButton>
                </div>

                <label className="flex items-start gap-fs-xs text-fs-body-md text-fs-text-subtle" htmlFor="labs-preview-consent">
                  <input checked={consented} className="mt-1 size-4 shrink-0 cursor-pointer" id="labs-preview-consent" onChange={e_0 => setConsented(e_0.target.checked)} type="checkbox" />
                  <span>Also email me new externships in this track. Unsubscribe any time.</span>
                </label>

                {error ? <p className="text-fs-body-md text-fs-danger" role="alert">
                    {error}
                  </p> : null}
              </>}
          </form>}

        {matches.length > 0 ? <div className="flex flex-col gap-fs-sm">
            <div className="flex flex-col gap-1">
              <h2 className="font-instrument text-[24px] text-fs-text leading-[1.15]">
                {isFieldLessPack(slug) ? 'Where that takes you next' : `Where that takes you in ${displayName}`}
              </h2>
              <p className="text-fs-body-md text-fs-text-subtle">
                Real companies, a project you can show people, and a certificate at the end. Open one to see what the
                work actually is. {TRIAL_TERMS}
              </p>
            </div>
            <div className={cn('grid gap-fs-md', matches.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1')}>
              {matches.slice(0, 2).map(rec_1 => <MatchCard accent={accent} key={rec_1.title} onOpen={handleMatchOpen} rec={rec_1} />)}
            </div>
          </div> : null}
      </div>
    </StepShell>;
}